import React from 'react';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import { Center, ScaleFade, Stack, Text } from '@chakra-ui/react';
import Button from '../button';

const noop = () => {};

export type State = {
  hasError: boolean;
  tryAgain: VoidFunction | null;
  message: Partial<{
    title: string;
    description: string;
    cta: string;
  }> | null;
};

class ErrorBoundary extends React.Component<
  React.PropsWithChildren<{ navigate: NavigateFunction }>,
  State
> {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      tryAgain: noop,
      message: null,
    };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  override componentDidCatch(error) {
    this.setState({
      hasError: true,
      tryAgain: error.tryAgain
        ? async () => {
            try {
              const cb = error.tryAgain ?? noop;
              cb();
              this.close();
            } catch (e) {}
          }
        : null,
      message: {
        title: error.message?.title ?? 'مشکلی پیش آمده است',
        description:
          error.message?.description ??
          'ارتباط اینترنت خود را چک کرده و مجددا امتحان کنید.',
        cta: error.message?.cta ?? 'تلاش مجدد',
      },
    });
  }

  close = () => this.setState({ hasError: false });

  override render() {
    const { hasError, message, tryAgain } = this.state;
    return !hasError ? (
      this.props.children
    ) : (
      <ScaleFade in={hasError}>
        <Center flexDirection={'column'} w={'100vw'} h={'100vh'}>
          <img src="/assets/not-found.svg" alt="not found" />
          <Text fontWeight={'bold'} mt={4} mb={2}>
            {message?.title}
          </Text>
          <Text fontSize={'smaller'} color={'darkgray'}>
            {message?.description}
          </Text>
          <Stack mt={6}>
            {tryAgain && (
              <Button
                minW={150}
                bgColor={'black'}
                color={'white'}
                onClick={tryAgain}
              >
                {message?.cta}
              </Button>
            )}
            <Button
              minW={150}
              variant={'ghost'}
              color={'black'}
              border={'1px solid'}
              onClick={() => {
                this.props.navigate(-1);
                setTimeout(() => {
                  this.close();
                }, 100);
              }}
            >
              بازگشت
            </Button>
          </Stack>
        </Center>
      </ScaleFade>
    );
  }
}

export default (props) => <ErrorBoundary {...props} navigate={useNavigate()} />;
